import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ingestUrl, uploadYouTube } from "../api/files";
import { useToast } from "../context/ToastContext";

const FILE_TYPES = ["Video", "PDF", "Audio", "Document", "Webpage"];

function StatusIndicator({ status }) {
  if (status === "idle") return null;

  const labels = {
    loading: { text: "Loading", color: "text-dark-text" },
    pending: { text: "Pending", color: "text-orange-400" },
    error: { text: "Error", color: "text-red-400" },
    successful: { text: "Successful", color: "text-accent" },
  };
  const cur = labels[status];

  return (
    <div className="flex items-center justify-center gap-3 mt-6 min-h-[28px]">
      {(status === "loading" || status === "pending") && (
        <div className="flex items-center gap-1">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="inline-block h-1.5 w-1.5 rounded-full bg-dark-muted animate-bounce"
              style={{ animationDelay: `${i * 0.18}s` }}
            />
          ))}
        </div>
      )}
      <span className={`text-sm font-medium ${cur.color}`}>{cur.text}</span>
    </div>
  );
}

export default function AttachLinkModal({ onClose, onFileAdded }) {
  const [fileType, setFileType] = useState("Video");
  const [url, setUrl] = useState("");
  const [status, setStatus] = useState("idle");
  const { toast } = useToast();
  const qc = useQueryClient();

  const handleAdd = async () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    if (!trimmed.startsWith("http://") && !trimmed.startsWith("https://")) {
      toast("URL must start with http:// or https://", "error");
      return;
    }

    setStatus("loading");
    try {
      const isYouTube =
        trimmed.includes("youtube.com/watch") || trimmed.includes("youtu.be/");

      setStatus("pending");
      let fileId = null;
      if (isYouTube) {
        const res = await uploadYouTube(trimmed, "");
        fileId = res.data?.data?.id;
      } else {
        const res = await ingestUrl(trimmed);
        fileId = res?.data?.id;
      }

      qc.invalidateQueries({ queryKey: ["files"] });
      setStatus("successful");
      setUrl("");
      toast("URL added — indexing in background", "success");
      if (fileId) onFileAdded?.(fileId);
      setTimeout(() => onClose(), 1000);
    } catch (err) {
      setStatus("error");
      toast(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          "Failed to add URL",
        "error"
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="w-[640px] rounded-2xl bg-[#232323] border border-dark-border p-7 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-7">
          <div className="flex items-center gap-3">
            <ion-icon
              name="link-outline"
              style={{ fontSize: "20px", color: "#e5e5e5" }}
            />
            <span className="text-base font-semibold">Attach Link</span>
          </div>
          <button
            onClick={onClose}
            className="text-dark-muted hover:text-dark-text transition-colors"
          >
            <ion-icon name="close-outline" style={{ fontSize: "22px" }} />
          </button>
        </div>

        {/* Input row */}
        <div className="flex items-center gap-3 mb-5">
          <label className="text-sm text-dark-muted flex-shrink-0">File Type</label>

          <div className="relative">
            <select
              value={fileType}
              onChange={(e) => setFileType(e.target.value)}
              className="appearance-none rounded-lg bg-[#2a2a2a] border border-dark-border px-3 py-2 pr-7 text-sm text-dark-text outline-none focus:border-[#3a3a3a] cursor-pointer"
            >
              {FILE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <ion-icon
              name="chevron-down-outline"
              style={{
                fontSize: "12px",
                color: "#666",
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            />
          </div>

          <input
            type="url"
            placeholder="https://..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            className="flex-1 rounded-lg bg-[#2a2a2a] border border-dark-border px-4 py-2 text-sm text-dark-text placeholder-[#555] outline-none focus:border-[#3a3a3a] transition-colors"
          />
        </div>

        {/* Add URL button */}
        <div className="flex justify-end">
          <button
            onClick={handleAdd}
            disabled={!url.trim() || status === "loading" || status === "pending"}
            className="rounded-full bg-accent px-5 py-2 text-sm font-semibold text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add URL
          </button>
        </div>

        <StatusIndicator status={status} />
      </div>
    </div>
  );
}
